import { Scales, Trophy, CheckCircle, X } from "@phosphor-icons/react";

const VEHICLES = ["401(k)", "TSP", "Roth IRA", "Traditional IRA", "Index Funds (Taxable)"];

const ROWS = [
  {
    label: "Who can use it",
    cells: [
      "Private-sector employees (if offered)",
      "Federal employees + military",
      "Anyone with earned income (income limits apply)",
      "Anyone with earned income",
      "Anyone with a brokerage account",
    ],
  },
  {
    label: "2026 contribution limit",
    cells: ["$23,500", "$23,500", "$7,000 ($8,000 if 50+)", "$7,000 ($8,000 if 50+)", "Unlimited"],
  },
  {
    label: "Employer match",
    cells: ["Often (free money)", "Up to 5% (FERS/BRS)", "No", "No", "No"],
  },
  {
    label: "Tax break — now",
    cells: ["Yes (Traditional)", "Yes (Traditional)", "No", "Yes (deductible)", "No"],
  },
  {
    label: "Tax break — at withdrawal",
    cells: ["No (Traditional taxed)", "No (Traditional taxed)", "Yes — 100% tax-free", "No (taxed)", "Capital gains rate"],
  },
  {
    label: "Investment menu",
    cells: ["Limited (employer menu)", "5 core + lifecycle funds", "Anything", "Anything", "Anything"],
  },
  {
    label: "Fees",
    cells: ["Varies — sometimes high", "~0.05% (lowest in industry)", "Brokerage choice (can be ~0%)", "Brokerage choice", "Fund choice (~0.03%+)"],
  },
  {
    label: "Withdraw penalty-free at",
    cells: ["59½", "59½ (or 55 if separated)", "59½ (contributions anytime)", "59½", "Anytime"],
  },
  {
    label: "Required withdrawals (RMDs)",
    cells: ["Age 73", "Age 73", "Never", "Age 73", "Never"],
  },
  {
    label: "Lawsuit protection",
    cells: ["Strong (ERISA)", "Strong (federal)", "Varies by state", "Varies by state", "Weak"],
  },
];

const TONE = {
  good: "text-emerald-300",
  warn: "text-amber-300",
  bad: "text-red-300",
};

// Heuristic tinting per cell: short labels that signal upside get green, downsides red.
function tintFor(text) {
  const t = text.toLowerCase();
  if (t === "no" || t.startsWith("never")) return "warn";
  if (t.includes("100% tax-free") || t.includes("free money") || t.includes("lowest") || t.includes("anything") || t.includes("strong")) return "good";
  if (t.includes("limited") || t.includes("weak") || t.includes("sometimes high") || t.includes("varies by state")) return "warn";
  return null;
}

export default function RetireCompare() {
  return (
    <div className="p-5 max-w-[1400px] space-y-6" data-testid="retire-compare-page">
      {/* Hero */}
      <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-sm bg-amber-900/20 flex items-center justify-center">
            <Scales size={20} weight="bold" className="text-amber-400" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-zinc-50">Retirement Vehicles — Side-by-Side</h2>
            <p className="text-xs text-zinc-400">All the major retirement accounts on one page</p>
          </div>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">
          The biggest mistake people make is picking the wrong account before the right investment. Use this table to see which vehicle fits your situation — then fill it with index funds (or whatever investment menu the account allows).
        </p>
      </div>

      {/* Comparison table */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-zinc-900/80 border-b border-amber-500/30">
                <th className="text-left text-[11px] uppercase tracking-wider text-amber-300 font-bold px-4 py-3 sticky left-0 bg-zinc-900/80 z-10">
                  Feature
                </th>
                {VEHICLES.map(v => (
                  <th key={v} className="text-left text-[11px] uppercase tracking-wider text-amber-300 font-bold px-4 py-3 whitespace-nowrap">
                    {v}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row, i) => (
                <tr key={row.label} className={`border-b border-zinc-800/40 ${i % 2 ? "bg-zinc-950/40" : "bg-zinc-900/20"}`}>
                  <td className="px-4 py-3 text-zinc-300 font-medium whitespace-nowrap sticky left-0 bg-inherit z-10">
                    {row.label}
                  </td>
                  {row.cells.map((cell, j) => {
                    const tone = tintFor(cell);
                    return (
                      <td key={j} className={`px-4 py-3 text-zinc-300 align-top ${tone ? TONE[tone] : ""}`}>
                        {cell}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recommended order */}
      <div className="bg-zinc-900/40 border border-amber-500/30 rounded-sm p-5">
        <h3 className="text-sm font-heading font-bold text-amber-300 mb-3 flex items-center gap-2">
          <Trophy size={14} weight="bold" /> The Standard Funding Order (Most People)
        </h3>
        <ol className="space-y-2.5 text-xs text-zinc-300 leading-relaxed list-decimal list-inside">
          <li><span className="font-bold text-zinc-100">Get the full employer match.</span> Contribute to your 401(k)/TSP up to the % that gets you the entire match. Free money first.</li>
          <li><span className="font-bold text-zinc-100">Max out a Roth IRA.</span> $7,000/year of tax-free growth for the rest of your life. Use Vanguard, Fidelity, or Schwab — fill it with index funds.</li>
          <li><span className="font-bold text-zinc-100">Go back to 401(k)/TSP.</span> Keep contributing until you hit the $23,500 annual limit if you can.</li>
          <li><span className="font-bold text-zinc-100">Open a taxable brokerage.</span> Once tax-advantaged accounts are maxed, invest extra savings in index funds in a regular brokerage.</li>
          <li><span className="font-bold text-zinc-100">HSA bonus.</span> If you have a high-deductible health plan, an HSA is the most tax-advantaged account in existence (triple tax-free).</li>
        </ol>
      </div>

      {/* TL;DR */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-emerald-900/10 border border-emerald-800/30 rounded-sm p-5">
          <h3 className="text-sm font-heading font-bold text-emerald-300 mb-2 flex items-center gap-2">
            <CheckCircle size={14} weight="fill" /> Best Tax Deal
          </h3>
          <p className="text-xs text-zinc-300 leading-relaxed">
            <span className="font-bold">Roth IRA</span> — pay tax once today, never pay again. Combined with 30+ years of index fund growth = enormous tax-free pile in retirement.
          </p>
        </div>
        <div className="bg-blue-900/10 border border-blue-800/30 rounded-sm p-5">
          <h3 className="text-sm font-heading font-bold text-blue-300 mb-2 flex items-center gap-2">
            <CheckCircle size={14} weight="fill" /> Best Free Money
          </h3>
          <p className="text-xs text-zinc-300 leading-relaxed">
            <span className="font-bold">401(k) / TSP match</span> — instant 50%-100% return on every dollar matched. No other investment comes close.
          </p>
        </div>
        <div className="bg-amber-900/10 border border-amber-800/30 rounded-sm p-5">
          <h3 className="text-sm font-heading font-bold text-amber-300 mb-2 flex items-center gap-2">
            <CheckCircle size={14} weight="fill" /> Best Flexibility
          </h3>
          <p className="text-xs text-zinc-300 leading-relaxed">
            <span className="font-bold">Taxable index funds</span> — no contribution limits, no withdrawal penalties, ultra-low fees. The "everything else" account.
          </p>
        </div>
      </div>
    </div>
  );
}
